import { useState, useMemo, Component, type ErrorInfo, type ReactNode } from 'react'
import { useQuery } from 'convex/react'
import { api } from '../../../../convex/_generated/api'
import { Id } from '../../../../convex/_generated/dataModel'
import { cn } from '@/lib/utils'
import { useAuth } from '@/contexts/auth-context'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { TOOL_CATALOG, type CatalogTool } from './toolCatalog.generated'
import {
  Activity,
  Check,
  ChevronDown,
  ChevronRight,
  Cpu,
  Filter,
  MessageSquare,
  Wrench,
  Zap,
  ArrowLeft,
  Search,
  X,
  AlertTriangle,
  FlaskConical,
} from 'lucide-react'

const FEATURE_LABELS: Record<string, string> = {
  agent_chat: 'Agent Chat',
  suggested_actions: 'Suggested Actions',
  prompt_summary: 'Prompt Summary',
  frame_editing: 'Frame Editing',
  video_clip_analysis: 'Video Clip Analysis',
  video_section_analysis: 'Video Section Analysis',
  image_analysis: 'Image Analysis',
  video_frame_analysis: 'Video Frame Analysis',
  video_segment_aggregate: 'Video Segment Aggregate',
  full_video_analysis: 'Full Video Analysis',
  frame_analysis: 'Frame Analysis',
  multi_clip_analysis: 'Multi-Clip Analysis',
  session_sub_journey: 'Session Journey',
  session_sub_friction: 'Session Friction',
  session_sub_correlation: 'Session Correlation',
  session_synthesis: 'Session Synthesis',
  session_segment_focused: 'Session Segment',
  session_auto_analyze: 'Session Auto-Analyze',
  compaction: 'Compaction',
}

const MODEL_SHORT: Record<string, string> = {
  'claude-opus-4-5-20251101': 'Opus 4.5',
  'claude-opus-4-6': 'Opus 4.6',
  'claude-sonnet-4-5-20250929': 'Sonnet 4.5',
  'claude-sonnet-4-5': 'Sonnet 4.5',
  'claude-sonnet-4-6': 'Sonnet 4.6',
  'claude-sonnet-4-20250514': 'Sonnet 4',
  'claude-haiku-4-5-20251001': 'Haiku 4.5',
  'gemini-2.0-flash': 'Gemini Flash',
}

const TOOL_CATEGORY_MAP: Record<string, string> = {}
for (const t of TOOL_CATALOG) {
  TOOL_CATEGORY_MAP[t.name] = t.category
}

const CATEGORY_COLORS: Record<string, string> = {
  Search: 'bg-blue-500/15 text-blue-400 border-blue-500/20',
  Project: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20',
  Timeline: 'bg-amber-500/15 text-amber-400 border-amber-500/20',
  Generation: 'bg-purple-500/15 text-purple-400 border-purple-500/20',
  Analysis: 'bg-cyan-500/15 text-cyan-400 border-cyan-500/20',
  Export: 'bg-rose-500/15 text-rose-400 border-rose-500/20',
  Billing: 'bg-orange-500/15 text-orange-400 border-orange-500/20',
  Assets: 'bg-teal-500/15 text-teal-400 border-teal-500/20',
  Audio: 'bg-pink-500/15 text-pink-400 border-pink-500/20',
  'Post-Production': 'bg-indigo-500/15 text-indigo-400 border-indigo-500/20',
  Media: 'bg-sky-500/15 text-sky-400 border-sky-500/20',
  Desktop: 'bg-stone-500/15 text-stone-400 border-stone-500/20',
  Visual: 'bg-violet-500/15 text-violet-400 border-violet-500/20',
  NUX: 'bg-lime-500/15 text-lime-400 border-lime-500/20',
}

function formatTokens(count: number): string {
  if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1)}M`
  if (count >= 1_000) return `${(count / 1_000).toFixed(1)}K`
  return count.toString()
}

function formatCost(cents: number): string {
  return `$${(cents / 100).toFixed(4)}`
}

function timeAgo(ts: number): string {
  const diff = Date.now() - ts
  if (diff < 60_000) return `${Math.floor(diff / 1000)}s ago`
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`
  return `${Math.floor(diff / 86_400_000)}d ago`
}

function formatTimestamp(ts: number): string {
  return new Date(ts).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

// ---- Rendering Helpers ----

const mdComponents = {
  p: ({ children }: any) => <p className="text-xs text-neutral-300 leading-relaxed mb-2 last:mb-0">{children}</p>,
  strong: ({ children }: any) => <strong className="text-neutral-200 font-semibold">{children}</strong>,
  em: ({ children }: any) => <em className="text-neutral-400 italic">{children}</em>,
  h1: ({ children }: any) => <h1 className="text-sm font-semibold text-neutral-200 mb-2 mt-3">{children}</h1>,
  h2: ({ children }: any) => <h2 className="text-xs font-semibold text-neutral-200 mb-1.5 mt-2">{children}</h2>,
  h3: ({ children }: any) => <h3 className="text-xs font-semibold text-neutral-300 mb-1 mt-2">{children}</h3>,
  ul: ({ children }: any) => <ul className="list-disc list-inside text-xs text-neutral-300 space-y-0.5 mb-2 ml-1">{children}</ul>,
  ol: ({ children }: any) => <ol className="list-decimal list-inside text-xs text-neutral-300 space-y-0.5 mb-2 ml-1">{children}</ol>,
  li: ({ children }: any) => <li className="text-xs text-neutral-300">{children}</li>,
  code: ({ children, className }: any) => {
    const isBlock = className?.includes('language-')
    if (isBlock) return <code className="block bg-neutral-950 rounded p-2 text-[11px] font-mono text-neutral-300 overflow-x-auto border border-neutral-800 my-1">{children}</code>
    return <code className="bg-neutral-800 px-1 py-0.5 rounded text-[11px] font-mono text-amber-300">{children}</code>
  },
  pre: ({ children }: any) => <div className="my-1">{children}</div>,
  blockquote: ({ children }: any) => <blockquote className="border-l-2 border-neutral-700 pl-3 my-2 text-neutral-400 italic">{children}</blockquote>,
  a: ({ href, children }: any) => <a href={href} className="text-blue-400 hover:underline" target="_blank" rel="noopener noreferrer">{children}</a>,
  hr: () => <hr className="border-neutral-800 my-2" />,
  table: ({ children }: any) => <table className="text-xs border-collapse w-full my-2">{children}</table>,
  th: ({ children }: any) => <th className="border border-neutral-700 px-2 py-1 bg-neutral-900 text-neutral-400 text-left font-medium">{children}</th>,
  td: ({ children }: any) => <td className="border border-neutral-800 px-2 py-1 text-neutral-300">{children}</td>,
}

function RenderValue({ value, depth = 0 }: { value: any; depth?: number }) {
  if (value === null || value === undefined) return <span className="text-neutral-600 italic">null</span>
  if (typeof value === 'boolean') return <span className={value ? 'text-emerald-400' : 'text-red-400'}>{value.toString()}</span>
  if (typeof value === 'number') return <span className="text-blue-400">{value.toLocaleString()}</span>
  if (typeof value === 'string') {
    if (value.length > 500 && depth > 0) {
      return <span className="text-neutral-300">{value.slice(0, 500)}...</span>
    }
    if (value.startsWith('http')) {
      return <a href={value} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline break-all">{value}</a>
    }
    if (value.length > 100) {
      return <span className="text-neutral-300 whitespace-pre-wrap">{value}</span>
    }
    return <span className="text-neutral-300">{value}</span>
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="text-neutral-600 italic">empty list</span>
    if (value.every(v => typeof v === 'string') && value.length <= 10) {
      return (
        <div className="flex flex-wrap gap-1">
          {value.map((v, i) => (
            <span key={i} className="bg-neutral-800 px-1.5 py-0.5 rounded text-[11px] text-neutral-300 border border-neutral-700">{v}</span>
          ))}
        </div>
      )
    }
    return (
      <div className="space-y-1 ml-2">
        {value.map((v, i) => (
          <div key={i} className="flex gap-2">
            <span className="text-neutral-600 text-[10px] mt-0.5 flex-shrink-0">{i + 1}.</span>
            <RenderValue value={v} depth={depth + 1} />
          </div>
        ))}
      </div>
    )
  }
  if (typeof value === 'object') {
    const entries = Object.entries(value)
    if (entries.length === 0) return <span className="text-neutral-600 italic">empty</span>
    return (
      <div className={cn('space-y-1', depth > 0 && 'ml-2 pl-2 border-l border-neutral-800')}>
        {entries.map(([k, v]) => (
          <div key={k} className="flex gap-2 items-start">
            <span className="text-neutral-500 text-[11px] font-mono flex-shrink-0 min-w-0">{k}</span>
            <div className="flex-1 min-w-0">
              <RenderValue value={v} depth={depth + 1} />
            </div>
          </div>
        ))}
      </div>
    )
  }
  return <span className="text-neutral-300">{String(value)}</span>
}

function ToolInputDisplay({ input }: { input: any }) {
  if (!input || typeof input !== 'object') return null
  const entries = Object.entries(input)
  if (entries.length === 0) return <span className="text-neutral-600 italic text-xs">no input</span>

  return (
    <div className="space-y-2">
      {entries.map(([key, value]) => {
        const isLongText = typeof value === 'string' && value.length > 120
        return (
          <div key={key}>
            <div className="flex items-baseline gap-2">
              <span className="text-[11px] font-mono text-amber-300/70 flex-shrink-0">{key}</span>
              {!isLongText && (
                <div className="text-xs flex-1 min-w-0">
                  <RenderValue value={value} />
                </div>
              )}
            </div>
            {isLongText && (
              <div className="mt-1 bg-neutral-950 rounded-md p-3 border border-neutral-800">
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
                  {value as string}
                </ReactMarkdown>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

function ToolOutputDisplay({ result }: { result: any }) {
  if (!result || typeof result !== 'object') return null

  const isSuccess = result.success !== false
  const hasError = result.error
  const { success, error, _input, ...rest } = result

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        {isSuccess ? (
          <span className="flex items-center gap-1 text-[11px] text-emerald-400"><Check className="w-3 h-3" /> Success</span>
        ) : (
          <span className="flex items-center gap-1 text-[11px] text-red-400"><AlertTriangle className="w-3 h-3" /> Failed</span>
        )}
        {hasError && <span className="text-[11px] text-red-300">{error}</span>}
      </div>
      {Object.keys(rest).length > 0 && (
        <div className="space-y-1.5">
          {Object.entries(rest).map(([key, value]) => {
            const isLongString = typeof value === 'string' && value.length > 200
            return (
              <div key={key}>
                <span className="text-[10px] text-neutral-500 uppercase tracking-wider">{key.replace(/_/g, ' ')}</span>
                {isLongString ? (
                  <div className="mt-1 bg-neutral-950 rounded-md p-3 border border-neutral-800 max-h-60 overflow-auto">
                    <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
                      {value as string}
                    </ReactMarkdown>
                  </div>
                ) : (
                  <div className="text-xs mt-0.5">
                    <RenderValue value={value} />
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ---- Error Boundary ----

class ObservabilityErrorBoundary extends Component<
  { children: ReactNode },
  { error: Error | null }
> {
  state: { error: Error | null } = { error: null }

  static getDerivedStateFromError(error: Error) {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[Observability] Error caught:', error, info)
  }

  render() {
    if (this.state.error) {
      return (
        <div className="flex flex-col items-center justify-center h-full gap-4 text-neutral-400">
          <AlertTriangle className="w-8 h-8 text-amber-500" />
          <p className="text-sm">Session error — this usually resolves after a page reload.</p>
          <p className="text-xs text-neutral-600 max-w-md text-center">{this.state.error?.message}</p>
          <button
            onClick={() => {
              this.setState({ error: null })
              window.location.reload()
            }}
            className="px-4 py-2 text-sm bg-neutral-800 hover:bg-neutral-700 rounded-lg transition-colors"
          >
            Reload page
          </button>
        </div>
      )
    }
    return this.props.children
  }
}

// ---- Main Page ----

export default function ObservabilityPage() {
  const { user } = useAuth()
  const [activeTab, setActiveTab] = useState<'calls' | 'runs' | 'tools'>('calls')

  if (!user?.isAdmin) {
    return (
      <div className="flex items-center justify-center h-full text-neutral-500">
        Admin access required
      </div>
    )
  }

  return (
    <ObservabilityErrorBoundary>
    <div className="h-full flex flex-col bg-neutral-950 text-neutral-100 overflow-hidden">
      <div className="border-b border-neutral-800 px-6 py-4 flex-shrink-0">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
            <Activity className="w-5 h-5 text-emerald-400" />
          </div>
          <div className="flex-1">
            <h1 className="text-lg font-semibold tracking-tight">LLM Observability</h1>
            <p className="text-xs text-neutral-500">Debug view — all API calls, agent conversations, and tool definitions</p>
          </div>
          <a
            href="/admin/agent"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-medium text-neutral-400 hover:text-neutral-200 bg-neutral-900 border border-neutral-800 hover:border-neutral-700 transition-colors"
          >
            <FlaskConical className="w-3 h-3" />
            Agent Playground
          </a>
        </div>

        <div className="flex gap-1 bg-neutral-900 rounded-lg p-1 w-fit">
          {[
            { id: 'calls' as const, label: 'LLM Calls', icon: Zap },
            { id: 'runs' as const, label: 'Agent Runs', icon: Cpu },
            { id: 'tools' as const, label: 'Tool Catalog', icon: Wrench, count: TOOL_CATALOG.length },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                'flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all',
                activeTab === tab.id
                  ? 'bg-neutral-800 text-white shadow-sm'
                  : 'text-neutral-500 hover:text-neutral-300'
              )}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {'count' in tab && tab.count != null && (
                <span className="text-[10px] bg-neutral-700 text-neutral-300 px-1.5 py-0.5 rounded-full">
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-hidden">
        {activeTab === 'calls' && <LLMCallsTab />}
        {activeTab === 'runs' && <AgentRunsTab />}
        {activeTab === 'tools' && <ToolCatalogTab />}
      </div>
    </div>
    </ObservabilityErrorBoundary>
  )
}

// ---- LLM Calls Tab ----

function LLMCallsTab() {
  const { user } = useAuth()
  const [featureFilter, setFeatureFilter] = useState<string>('')
  const [modelFilter, setModelFilter] = useState<string>('')
  const [selectedCall, setSelectedCall] = useState<any>(null)

  const filters = useQuery(api.observability.getDistinctFeatures, user?.isAdmin ? {} : "skip")
  const data = useQuery(api.observability.listLLMCalls, user?.isAdmin ? {
    limit: 30,
    feature: featureFilter || undefined,
    model: modelFilter || undefined,
  } : "skip")

  if (selectedCall) {
    return <LLMCallDetail call={selectedCall} onBack={() => setSelectedCall(null)} />
  }

  return (
    <div className="h-full flex flex-col">
      {/* Filters */}
      <div className="px-6 py-3 border-b border-neutral-800 flex items-center gap-3 flex-shrink-0">
        <Filter className="w-4 h-4 text-neutral-500" />
        <select
          value={featureFilter}
          onChange={(e) => setFeatureFilter(e.target.value)}
          className="bg-neutral-900 border border-neutral-700 rounded-md px-3 py-1.5 text-sm text-neutral-300 focus:outline-none focus:border-neutral-500"
        >
          <option value="">All features</option>
          {filters?.features.map((f) => (
            <option key={f} value={f}>{FEATURE_LABELS[f] ?? f}</option>
          ))}
        </select>
        <select
          value={modelFilter}
          onChange={(e) => setModelFilter(e.target.value)}
          className="bg-neutral-900 border border-neutral-700 rounded-md px-3 py-1.5 text-sm text-neutral-300 focus:outline-none focus:border-neutral-500"
        >
          <option value="">All models</option>
          {filters?.models.map((m) => (
            <option key={m} value={m}>{MODEL_SHORT[m] ?? m}</option>
          ))}
        </select>
        {(featureFilter || modelFilter) && (
          <button
            onClick={() => { setFeatureFilter(''); setModelFilter('') }}
            className="text-xs text-neutral-500 hover:text-neutral-300 flex items-center gap-1"
          >
            <X className="w-3 h-3" /> Clear
          </button>
        )}
        {data && (
          <span className="ml-auto text-xs text-neutral-600">{data.calls.length} calls</span>
        )}
      </div>

      {/* Aggregate list view */}
      <div className="flex-1 overflow-auto">
        {!data ? (
          <div className="flex items-center justify-center h-32 text-neutral-600 text-sm">Loading...</div>
        ) : data.calls.length === 0 ? (
          <div className="flex items-center justify-center h-32 text-neutral-600 text-sm">No calls found</div>
        ) : (
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-neutral-900 border-b border-neutral-800 z-10">
              <tr className="text-left text-neutral-500">
                <th className="px-4 py-2.5 font-medium">Time</th>
                <th className="px-4 py-2.5 font-medium">Feature</th>
                <th className="px-4 py-2.5 font-medium">Model</th>
                <th className="px-4 py-2.5 font-medium text-right">Input</th>
                <th className="px-4 py-2.5 font-medium text-right">Output</th>
                <th className="px-4 py-2.5 font-medium text-right">Cache R</th>
                <th className="px-4 py-2.5 font-medium text-right">Cost</th>
                <th className="px-4 py-2.5 font-medium">User</th>
                <th className="px-4 py-2.5 font-medium">Video</th>
                <th className="px-4 py-2.5 font-medium">Skills</th>
                <th className="px-4 py-2.5 font-medium text-center">Data</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-800/50">
              {data.calls.map((call) => {
                const modelColor = call.model.includes('opus')
                  ? 'text-amber-400'
                  : call.model.includes('haiku')
                    ? 'text-green-400'
                    : call.model.includes('gemini')
                      ? 'text-blue-400'
                      : 'text-purple-400'
                const hasData = call.hasData
                return (
                  <tr
                    key={call._id}
                    onClick={() => setSelectedCall(call)}
                    className="hover:bg-neutral-800/50 cursor-pointer transition-colors"
                  >
                    <td className="px-4 py-2.5 font-mono text-neutral-500 whitespace-nowrap">{formatTimestamp(call.timestamp)}</td>
                    <td className="px-4 py-2.5">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-neutral-800 border border-neutral-700 text-neutral-300 font-medium">
                        {FEATURE_LABELS[call.feature] ?? call.feature}
                      </span>
                    </td>
                    <td className={cn('px-4 py-2.5 font-mono font-semibold whitespace-nowrap', modelColor)}>
                      {MODEL_SHORT[call.model] ?? call.model}
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono text-neutral-400">{formatTokens(call.inputTokens)}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-neutral-400">{formatTokens(call.outputTokens)}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-neutral-500">
                      {call.cacheReadTokens > 0 ? formatTokens(call.cacheReadTokens) : '-'}
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono text-neutral-200">{formatCost(call.costCents)}</td>
                    <td className="px-4 py-2.5 text-neutral-500 max-w-[150px] truncate">{call.userName || call.userEmail || '-'}</td>
                    <td className="px-4 py-2.5 text-neutral-600 max-w-[150px] truncate">{call.workflowTitle || '-'}</td>
                    <td className="px-4 py-2.5">
                      {call.activeSkills?.length > 0 ? (
                        <div className="flex flex-wrap gap-0.5">
                          {call.activeSkills.map((s: string) => (
                            <span key={s} className="text-[9px] px-1 py-0.5 rounded bg-cyan-500/10 text-cyan-400 border border-cyan-500/15">{s}</span>
                          ))}
                        </div>
                      ) : <span className="text-neutral-700">-</span>}
                    </td>
                    <td className="px-4 py-2.5 text-center">
                      {hasData ? (
                        <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" title="Has prompt data" />
                      ) : (
                        <span className="inline-block w-2 h-2 rounded-full bg-neutral-700" title="No prompt data" />
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

function LLMCallDetail({ call, onBack }: { call: any; onBack: () => void }) {
  const detail = useQuery(api.observability.getLLMCallDetail, { callId: call._id })

  const enrichedCall = useMemo(() => ({
    ...call,
    systemPrompt: detail?.systemPrompt,
    messages: detail?.messages,
    tools: detail?.tools,
    responseText: detail?.responseText,
  }), [call, detail])

  return (
    <div className="h-full flex flex-col">
      <div className="px-6 py-3 border-b border-neutral-800 flex items-center gap-3 flex-shrink-0">
        <button onClick={onBack} className="p-1.5 rounded-md hover:bg-neutral-800 transition-colors">
          <ArrowLeft className="w-4 h-4 text-neutral-400" />
        </button>
        <div className="flex-1 min-w-0 flex items-center gap-3 flex-wrap">
          <span className="text-xs font-mono text-neutral-500">{formatTimestamp(call.timestamp)}</span>
          <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-neutral-800 border border-neutral-700">
            {FEATURE_LABELS[call.feature] ?? call.feature}
          </span>
          <span className={cn('text-xs font-mono font-semibold',
            call.model.includes('opus') ? 'text-amber-400' :
            call.model.includes('haiku') ? 'text-green-400' :
            call.model.includes('gemini') ? 'text-blue-400' : 'text-purple-400'
          )}>{MODEL_SHORT[call.model] ?? call.model}</span>
          <span className="text-xs font-mono text-neutral-200">{formatCost(call.costCents)}</span>
          {call.userEmail && <span className="text-xs text-neutral-500">{call.userName ? `${call.userName} ` : ''}{call.userEmail}</span>}
          {call.workflowTitle && <span className="text-xs text-neutral-600">{call.workflowTitle}</span>}
          {call.activeSkills?.length > 0 && (
            <div className="flex gap-1">
              {call.activeSkills.map((s: string) => (
                <span key={s} className="text-[10px] px-1.5 py-0.5 rounded bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">{s}</span>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="flex-1 overflow-auto">
        {detail === undefined ? (
          <div className="flex items-center justify-center h-32 text-neutral-600 text-sm">Loading details...</div>
        ) : (
          <LLMCallCard call={enrichedCall} />
        )}
      </div>
    </div>
  )
}

function CollapsibleSection({
  label,
  count,
  colorClass,
  bgClass,
  isOpen,
  onToggle,
  children,
}: {
  label: string
  count?: string
  colorClass: string
  bgClass: string
  isOpen: boolean
  onToggle: () => void
  children: React.ReactNode
}) {
  return (
    <div className="border-b border-neutral-800/50">
      <button
        onClick={onToggle}
        className={cn('w-full px-4 py-2 flex items-center gap-2 text-left hover:brightness-125 transition-all', bgClass)}
      >
        {isOpen ? <ChevronDown className="w-3 h-3 text-neutral-500 flex-shrink-0" /> : <ChevronRight className="w-3 h-3 text-neutral-500 flex-shrink-0" />}
        <span className={cn('text-[10px] uppercase tracking-wider font-semibold', colorClass)}>{label}</span>
        {count && <span className="text-[10px] text-neutral-600">{count}</span>}
      </button>
      {isOpen && children}
    </div>
  )
}

function LLMCallCard({ call }: { call: any }) {
  const modelColor = call.model.includes('opus')
    ? 'text-amber-400'
    : call.model.includes('haiku')
      ? 'text-green-400'
      : call.model.includes('gemini')
        ? 'text-blue-400'
        : 'text-purple-400'

  const parsedMessages = useMemo(() => {
    if (!call.messages) return null
    try { return JSON.parse(call.messages) } catch { return null }
  }, [call.messages])

  const parsedTools = useMemo(() => {
    if (!call.tools) return null
    try { return JSON.parse(call.tools) } catch { return null }
  }, [call.tools])

  const hasPromptData = call.systemPrompt || parsedMessages || parsedTools || call.responseText

  const sections = useMemo(() => {
    const s: string[] = []
    if (call.systemPrompt) s.push('system')
    if (parsedMessages?.length) s.push('messages')
    if (parsedTools?.length) s.push('tools')
    if (call.responseText) s.push('response')
    return s
  }, [call.systemPrompt, parsedMessages, parsedTools, call.responseText])

  const [openSections, setOpenSections] = useState<Set<string>>(new Set())

  const allOpen = sections.length > 0 && sections.every(s => openSections.has(s))

  const toggleSection = (key: string) => {
    setOpenSections(prev => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const toggleAll = () => {
    if (allOpen) {
      setOpenSections(new Set())
    } else {
      setOpenSections(new Set(sections))
    }
  }

  return (
    <div className="rounded-lg border border-neutral-800 bg-neutral-900/50">
      {/* Header bar with metadata */}
      <div className="px-4 py-3 flex items-center gap-3 flex-wrap border-b border-neutral-800/50">
        <span className="text-xs font-mono text-neutral-500">{formatTimestamp(call.timestamp)}</span>
        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-xs font-medium bg-neutral-800 border border-neutral-700">
          {FEATURE_LABELS[call.feature] ?? call.feature}
        </span>
        <span className={cn('text-xs font-mono font-semibold', modelColor)}>{MODEL_SHORT[call.model] ?? call.model}</span>
        <span className="text-[10px] text-neutral-500">
          in:{formatTokens(call.inputTokens)} out:{formatTokens(call.outputTokens)}
          {call.cacheReadTokens > 0 && <> cacheR:{formatTokens(call.cacheReadTokens)}</>}
          {call.cacheCreationTokens > 0 && <> cacheW:{formatTokens(call.cacheCreationTokens)}</>}
        </span>
        <span className="text-xs font-mono text-neutral-200">{formatCost(call.costCents)}</span>
        {call.userEmail && <span className="text-xs text-neutral-500 ml-auto">{call.userName ? `${call.userName} ` : ''}{call.userEmail}</span>}
        {call.workflowTitle && <span className="text-xs text-neutral-600">{call.workflowTitle}</span>}
        {sections.length > 0 && (
          <button
            onClick={toggleAll}
            className="text-[10px] text-neutral-500 hover:text-neutral-300 px-2 py-0.5 rounded border border-neutral-700 hover:border-neutral-600 transition-colors"
          >
            {allOpen ? 'Collapse All' : 'Expand All'}
          </button>
        )}
      </div>

      {!hasPromptData && (
        <div className="px-4 py-3 text-xs text-neutral-600 italic">
          No prompt data logged (pre-observability call). New calls will include full system prompt, messages, tools, and response.
        </div>
      )}

      {/* System Prompt */}
      {call.systemPrompt && (
        <CollapsibleSection
          label="System Prompt"
          count={`${call.systemPrompt.length} chars`}
          colorClass="text-yellow-400/70"
          bgClass="bg-yellow-500/5"
          isOpen={openSections.has('system')}
          onToggle={() => toggleSection('system')}
        >
          <div className="px-4 py-3">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
              {call.systemPrompt}
            </ReactMarkdown>
          </div>
        </CollapsibleSection>
      )}

      {/* Messages */}
      {parsedMessages && Array.isArray(parsedMessages) && (
        <CollapsibleSection
          label="Messages"
          count={`${parsedMessages.length} messages`}
          colorClass="text-blue-400/70"
          bgClass="bg-blue-500/5"
          isOpen={openSections.has('messages')}
          onToggle={() => toggleSection('messages')}
        >
          <div className="space-y-1 px-4 py-3">
            {parsedMessages.map((msg: any, i: number) => (
              <div key={i} className={cn(
                'rounded-md border p-3',
                msg.role === 'user' ? 'border-blue-500/15 bg-blue-500/5' :
                msg.role === 'assistant' ? 'border-purple-500/15 bg-purple-500/5' :
                'border-neutral-800 bg-neutral-900/50'
              )}>
                <div className="flex items-center gap-2 mb-1">
                  <span className={cn(
                    'text-[9px] uppercase tracking-wider font-semibold px-1 py-0.5 rounded',
                    msg.role === 'user' ? 'bg-blue-500/20 text-blue-400' :
                    msg.role === 'assistant' ? 'bg-purple-500/20 text-purple-400' :
                    'bg-neutral-700 text-neutral-400'
                  )}>{msg.role}</span>
                </div>
                {typeof msg.content === 'string' ? (
                  msg.role === 'user' ? (
                    <p className="text-sm text-neutral-200 whitespace-pre-wrap">{msg.content}</p>
                  ) : (
                    <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
                      {msg.content}
                    </ReactMarkdown>
                  )
                ) : Array.isArray(msg.content) ? (
                  <div className="space-y-1">
                    {msg.content.map((block: any, bi: number) => (
                      <div key={bi}>
                        {block.type === 'text' && (
                          <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
                            {block.text}
                          </ReactMarkdown>
                        )}
                        {block.type === 'tool_use' && (
                          <div className="bg-neutral-950 rounded p-2 border border-neutral-800 text-xs">
                            <span className="font-mono text-amber-300">{block.name}</span>
                            <div className="mt-1"><ToolInputDisplay input={block.input} /></div>
                          </div>
                        )}
                        {block.type === 'tool_result' && (
                          <div className="bg-neutral-950 rounded p-2 border border-neutral-800 text-xs">
                            <span className="text-neutral-500">tool_result [{block.tool_use_id?.slice(0, 8)}]</span>
                            {typeof block.content === 'string' ? (
                              <p className="text-neutral-300 mt-1 whitespace-pre-wrap text-[11px]">{block.content.slice(0, 2000)}{block.content.length > 2000 ? '...' : ''}</p>
                            ) : (
                              <div className="mt-1"><RenderValue value={block.content} /></div>
                            )}
                          </div>
                        )}
                        {block.type === 'image' && (
                          <span className="text-[10px] text-neutral-500">[image: {block.source?.type}]</span>
                        )}
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </CollapsibleSection>
      )}

      {/* Tools */}
      {parsedTools && Array.isArray(parsedTools) && (
        <CollapsibleSection
          label="Tools"
          count={`${parsedTools.length} tools`}
          colorClass="text-amber-400/70"
          bgClass="bg-amber-500/5"
          isOpen={openSections.has('tools')}
          onToggle={() => toggleSection('tools')}
        >
          <div className="px-4 py-3 space-y-2">
            {parsedTools.map((tool: any, i: number) => (
              <div key={i} className="rounded-md border border-neutral-800 bg-neutral-950 p-3">
                <div className="flex items-center gap-2 mb-1">
                  <code className="text-xs font-mono text-amber-300 font-semibold">{tool.name ?? tool.type}</code>
                  {TOOL_CATEGORY_MAP[tool.name] && (
                    <span className={cn('text-[9px] px-1.5 py-0.5 rounded border', CATEGORY_COLORS[TOOL_CATEGORY_MAP[tool.name]] ?? '')}>
                      {TOOL_CATEGORY_MAP[tool.name]}
                    </span>
                  )}
                </div>
                {tool.description && (
                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
                    {tool.description}
                  </ReactMarkdown>
                )}
                {tool.input_schema?.properties && (
                  <div className="mt-2 space-y-0.5">
                    {Object.entries(tool.input_schema.properties).map(([k, v]: [string, any]) => (
                      <div key={k} className="flex items-baseline gap-2 text-[11px]">
                        <code className="font-mono text-neutral-500">{k}</code>
                        <span className="font-mono text-purple-400/50">{v.type}</span>
                        {v.description && <span className="text-neutral-600 truncate">{v.description.slice(0, 100)}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </CollapsibleSection>
      )}

      {/* Response */}
      {call.responseText && (
        <CollapsibleSection
          label="Response"
          count={`${call.responseText.length} chars`}
          colorClass="text-emerald-400/70"
          bgClass="bg-emerald-500/5"
          isOpen={openSections.has('response')}
          onToggle={() => toggleSection('response')}
        >
          <div className="px-4 py-3">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
              {call.responseText}
            </ReactMarkdown>
          </div>
        </CollapsibleSection>
      )}
    </div>
  )
}

// ---- Agent Runs Tab ----

function AgentRunsTab() {
  const { user } = useAuth()
  const [selectedWorkflow, setSelectedWorkflow] = useState<string | null>(null)

  const runs = useQuery(api.observability.listRecentAgentRuns, user?.isAdmin ? { limit: 30 } : "skip")

  if (selectedWorkflow) {
    return (
      <AgentRunDetail
        workflowId={selectedWorkflow as Id<"workflows">}
        onBack={() => setSelectedWorkflow(null)}
      />
    )
  }

  return (
    <div className="h-full overflow-auto">
      {!runs ? (
        <div className="flex items-center justify-center h-32 text-neutral-600 text-sm">Loading...</div>
      ) : (
        <div className="divide-y divide-neutral-800/50">
          {runs.map((run) => (
            <button
              key={run.workflowId}
              onClick={() => setSelectedWorkflow(run.workflowId)}
              className="w-full text-left px-6 py-4 hover:bg-neutral-900/50 transition-colors flex items-center gap-4"
            >
              <div className="p-2 rounded-lg bg-neutral-800 border border-neutral-700">
                <Cpu className="w-4 h-4 text-purple-400" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-neutral-200 truncate">
                    {run.workflowTitle || 'Untitled'}
                  </span>
                  <span className="text-[10px] text-neutral-600 font-mono">{run.workflowId.slice(0, 10)}</span>
                </div>
                <div className="flex items-center gap-3 mt-1 text-xs text-neutral-500">
                  <span>{run.userEmail}</span>
                  <span className="text-neutral-700">|</span>
                  <span className="flex items-center gap-1"><MessageSquare className="w-3 h-3" />{run.messageCount} msgs</span>
                  <span className="text-neutral-700">|</span>
                  <span className="flex items-center gap-1"><Wrench className="w-3 h-3" />{run.toolCallCount} tool calls</span>
                </div>
              </div>
              <div className="text-xs text-neutral-600 font-mono flex-shrink-0">{timeAgo(run.lastMessageAt)}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

function AgentRunDetail({
  workflowId,
  onBack,
}: {
  workflowId: Id<"workflows">
  onBack: () => void
}) {
  const { user } = useAuth()
  const data = useQuery(api.observability.getWorkflowMessages, user?.isAdmin ? { workflowId } : "skip")
  const [searchQuery, setSearchQuery] = useState('')
  const [expandAllTools, setExpandAllTools] = useState(false)

  const filteredMessages = useMemo(() => {
    if (!data?.messages || !searchQuery) return data?.messages ?? []
    const q = searchQuery.toLowerCase()
    return data.messages.filter(
      (m) =>
        m.content.toLowerCase().includes(q) ||
        m.toolCalls.some((tc: any) => tc.name.toLowerCase().includes(q) || JSON.stringify(tc.input).toLowerCase().includes(q)) ||
        m.toolResults.some((tr: any) => JSON.stringify(tr.result).toLowerCase().includes(q))
    )
  }, [data?.messages, searchQuery])

  if (!data) {
    return <div className="flex items-center justify-center h-32 text-neutral-600 text-sm">Loading...</div>
  }

  return (
    <div className="h-full flex flex-col">
      <div className="px-6 py-3 border-b border-neutral-800 flex items-center gap-3 flex-shrink-0">
        <button onClick={onBack} className="p-1.5 rounded-md hover:bg-neutral-800 transition-colors">
          <ArrowLeft className="w-4 h-4 text-neutral-400" />
        </button>
        <div className="flex-1 min-w-0">
          <h2 className="text-sm font-medium text-neutral-200 truncate">
            {data.workflow.title || 'Untitled'}
          </h2>
          <div className="flex items-center gap-2 text-xs text-neutral-500 mt-0.5">
            <span>{data.workflow.userEmail}</span>
            <span className="text-neutral-700">|</span>
            <span>{data.messages.length} messages</span>
            {data.workflow.isDesktop && <><span className="text-neutral-700">|</span><span className="text-orange-400">Desktop</span></>}
            {data.workflow.isNux && <><span className="text-neutral-700">|</span><span className="text-lime-400">NUX</span></>}
          </div>
        </div>
        <button
          onClick={() => setExpandAllTools(v => !v)}
          className="text-[10px] text-neutral-500 hover:text-neutral-300 px-2 py-1 rounded border border-neutral-700 hover:border-neutral-600 transition-colors flex items-center gap-1"
        >
          <Wrench className="w-3 h-3" />
          {expandAllTools ? 'Collapse Tools' : 'Expand Tools'}
        </button>
        <div className="relative">
          <Search className="w-3.5 h-3.5 text-neutral-600 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="bg-neutral-900 border border-neutral-700 rounded-md pl-8 pr-3 py-1.5 text-xs text-neutral-300 w-48 focus:outline-none focus:border-neutral-500"
          />
        </div>
      </div>

      <div className="flex-1 overflow-auto px-6 py-4 space-y-2">
        {filteredMessages.map((msg: any) => (
          <MessageBlock key={msg._id} msg={msg} forceExpandTools={expandAllTools} />
        ))}
      </div>
    </div>
  )
}

function MessageBlock({ msg, forceExpandTools = false }: { msg: any; forceExpandTools?: boolean }) {
  const isUser = msg.role === 'user'
  const isSystem = msg.role === 'system'
  const [expandedTools, setExpandedTools] = useState<Set<string>>(new Set())

  const toolResultMap = useMemo(() => {
    const map: Record<string, any> = {}
    for (const tr of msg.toolResults) {
      map[tr.toolCallId] = tr.result
    }
    return map
  }, [msg.toolResults])

  const toggleTool = (id: string) => {
    setExpandedTools(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  return (
    <div className={cn('rounded-lg border', isUser ? 'border-blue-500/15 bg-blue-500/5' : isSystem ? 'border-yellow-500/15 bg-yellow-500/5' : 'border-neutral-800 bg-neutral-900/50')}>
      {/* Message header */}
      <div className="flex items-center gap-2 px-4 py-2 border-b border-neutral-800/50">
        <span className={cn(
          'text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded',
          isUser ? 'bg-blue-500/20 text-blue-400' : isSystem ? 'bg-yellow-500/20 text-yellow-400' : 'bg-purple-500/20 text-purple-400'
        )}>
          {msg.role}
        </span>
        <span className="text-[10px] text-neutral-600 font-mono">{formatTimestamp(msg.createdAt)}</span>
        {msg.isStreaming && <span className="text-[10px] text-amber-500 animate-pulse">streaming</span>}
        {msg.aborted && <span className="text-[10px] text-red-500">aborted</span>}
        {msg.attachments.length > 0 && (
          <span className="text-[10px] text-neutral-500">{msg.attachments.length} attachment(s)</span>
        )}
        {msg.content && <span className="text-[10px] text-neutral-700 ml-auto">{msg.content.length} chars</span>}
      </div>

      {/* Full content — always shown, no truncation */}
      {msg.content && (
        <div className="px-4 py-3">
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
            {msg.content}
          </ReactMarkdown>
        </div>
      )}

      {/* Attachments */}
      {msg.attachments.length > 0 && (
        <div className="px-4 pb-3 space-y-2">
          {msg.attachments.map((att: any, i: number) => (
            <div key={i} className="flex items-center gap-2 text-xs">
              <span className="text-neutral-500">Attachment {i + 1}:</span>
              {att.url && <a href={att.url} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline break-all">{att.name || att.url}</a>}
              {att.type && <span className="text-neutral-600">[{att.type}]</span>}
            </div>
          ))}
        </div>
      )}

      {/* Tool calls — collapsed by default, individually expandable */}
      {msg.toolCalls.length > 0 && (
        <div className="border-t border-neutral-800/50">
          {msg.toolCalls.map((tc: any) => {
            const result = toolResultMap[tc.id]
            const cat = TOOL_CATEGORY_MAP[tc.name]
            const catColor = cat
              ? CATEGORY_COLORS[cat] ?? 'bg-neutral-500/15 text-neutral-400 border-neutral-500/20'
              : 'bg-neutral-500/15 text-neutral-400 border-neutral-500/20'
            const isOpen = forceExpandTools || expandedTools.has(tc.id)

            return (
              <div key={tc.id} className="border-b border-neutral-800/30 last:border-b-0">
                <button
                  onClick={() => toggleTool(tc.id)}
                  className="w-full px-4 py-2 flex items-center gap-2 bg-neutral-800/20 hover:bg-neutral-800/40 transition-colors text-left"
                >
                  {isOpen ? <ChevronDown className="w-3 h-3 text-neutral-500 flex-shrink-0" /> : <ChevronRight className="w-3 h-3 text-neutral-500 flex-shrink-0" />}
                  <Wrench className="w-3 h-3 text-neutral-500 flex-shrink-0" />
                  <span className="text-xs font-mono text-amber-300 font-semibold">{tc.name}</span>
                  {cat && (
                    <span className={cn('text-[9px] px-1.5 py-0.5 rounded border', catColor)}>{cat}</span>
                  )}
                  {result && (
                    <span className={cn(
                      'text-[9px] px-1.5 py-0.5 rounded ml-auto',
                      result.success !== false ? 'bg-emerald-500/15 text-emerald-400' : 'bg-red-500/15 text-red-400'
                    )}>
                      {result.success !== false ? 'success' : 'error'}
                    </span>
                  )}
                </button>

                {isOpen && (
                  <div className="px-4 py-2 space-y-3">
                    <div>
                      <span className="text-[10px] text-neutral-500 uppercase tracking-wider">Input</span>
                      <div className="mt-1.5 bg-neutral-950 rounded-md p-3 border border-neutral-800">
                        <ToolInputDisplay input={tc.input} />
                      </div>
                    </div>
                    {result && (
                      <div>
                        <span className="text-[10px] text-neutral-500 uppercase tracking-wider">Output</span>
                        <div className="mt-1.5 bg-neutral-950 rounded-md p-3 border border-neutral-800">
                          <ToolOutputDisplay result={result} />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ---- Tool Catalog Tab ----

function ToolCatalogTab() {
  const [selectedCategory, setSelectedCategory] = useState<string>('')
  const [searchQuery, setSearchQuery] = useState('')

  const categories = useMemo(() => {
    return [...new Set(TOOL_CATALOG.map((t) => t.category))].sort()
  }, [])

  const filteredTools = useMemo(() => {
    return TOOL_CATALOG.filter((t) => {
      if (selectedCategory && t.category !== selectedCategory) return false
      if (searchQuery) {
        const q = searchQuery.toLowerCase()
        return t.name.toLowerCase().includes(q) || t.description.toLowerCase().includes(q) || t.category.toLowerCase().includes(q)
      }
      return true
    })
  }, [selectedCategory, searchQuery])

  const groupedTools = useMemo(() => {
    const groups: Record<string, CatalogTool[]> = {}
    for (const t of filteredTools) {
      if (!groups[t.category]) groups[t.category] = []
      groups[t.category].push(t)
    }
    return groups
  }, [filteredTools])

  return (
    <div className="h-full flex flex-col">
      <div className="px-6 py-3 border-b border-neutral-800 flex items-center gap-3 flex-shrink-0">
        <div className="relative">
          <Search className="w-3.5 h-3.5 text-neutral-600 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search tools..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="bg-neutral-900 border border-neutral-700 rounded-md pl-8 pr-3 py-1.5 text-xs text-neutral-300 w-64 focus:outline-none focus:border-neutral-500"
          />
        </div>
        <div className="flex gap-1 flex-wrap">
          <button
            onClick={() => setSelectedCategory('')}
            className={cn(
              'text-[10px] px-2 py-1 rounded-md border transition-colors',
              !selectedCategory
                ? 'bg-neutral-700 border-neutral-600 text-white'
                : 'bg-neutral-900 border-neutral-800 text-neutral-500 hover:text-neutral-300'
            )}
          >
            All ({TOOL_CATALOG.length})
          </button>
          {categories.map((cat) => {
            const count = TOOL_CATALOG.filter((t) => t.category === cat).length
            const catColor = CATEGORY_COLORS[cat] ?? 'bg-neutral-500/15 text-neutral-400 border-neutral-500/20'
            return (
              <button
                key={cat}
                onClick={() => setSelectedCategory(selectedCategory === cat ? '' : cat)}
                className={cn(
                  'text-[10px] px-2 py-1 rounded-md border transition-colors',
                  selectedCategory === cat ? catColor : 'bg-neutral-900 border-neutral-800 text-neutral-500 hover:text-neutral-300'
                )}
              >
                {cat} ({count})
              </button>
            )
          })}
        </div>
      </div>

      {/* All tools — fully expanded, no collapsing */}
      <div className="flex-1 overflow-auto px-6 py-4">
        {Object.entries(groupedTools).map(([category, tools]) => (
          <div key={category} className="mb-8">
            <h3 className="text-xs uppercase tracking-wider text-neutral-500 font-semibold mb-3 flex items-center gap-2">
              <span className={cn('w-2 h-2 rounded-full', CATEGORY_COLORS[category]?.split(' ')[0] ?? 'bg-neutral-500')} />
              {category}
              <span className="text-neutral-700">({tools.length})</span>
            </h3>
            <div className="space-y-3">
              {tools.map((tool) => (
                <ToolCard key={tool.name} tool={tool} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function ToolCard({ tool }: { tool: CatalogTool }) {
  const descriptionLines = tool.description.split('\n')
  const firstLine = descriptionLines[0]
  const hasExtendedDesc = descriptionLines.length > 1 && descriptionLines.slice(1).some(l => l.trim())
  const extendedDesc = hasExtendedDesc ? descriptionLines.slice(1).join('\n') : ''

  return (
    <div className="rounded-lg border border-neutral-800 bg-neutral-900/50">
      <div className="px-4 py-3">
        <div className="flex items-center gap-2 mb-1.5">
          <code className="text-sm font-mono text-amber-300 font-semibold">{tool.name}</code>
          <span className={cn('text-[9px] px-1.5 py-0.5 rounded border', CATEGORY_COLORS[tool.category] ?? 'bg-neutral-500/15 text-neutral-400 border-neutral-500/20')}>
            {tool.category}
          </span>
          {tool.isBuiltIn && (
            <span className="text-[9px] px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-400 border border-blue-500/20">built-in</span>
          )}
        </div>
        <p className="text-xs text-neutral-400 leading-relaxed">{firstLine}</p>
      </div>

      {hasExtendedDesc && (
        <div className="px-4 pb-3">
          <div className="bg-neutral-950 rounded-md p-3 border border-neutral-800">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
              {extendedDesc}
            </ReactMarkdown>
          </div>
        </div>
      )}

      {tool.params.length > 0 && (
        <div className="border-t border-neutral-800/50 px-4 py-3">
          <span className="text-[10px] text-neutral-500 uppercase tracking-wider font-medium">Parameters</span>
          <div className="mt-2 space-y-0">
            {tool.params.map((param) => (
              <div key={param.name} className="py-1.5 border-b border-neutral-800/30 last:border-b-0">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <code className="text-[11px] font-mono text-amber-300/80">{param.name}</code>
                  <span className="text-[10px] font-mono text-purple-400/70">{param.type}</span>
                  {param.required && <span className="text-[9px] text-red-400/70 bg-red-500/10 px-1 py-0.5 rounded">required</span>}
                  {param.enum && (
                    <div className="flex flex-wrap gap-0.5 ml-1">
                      {param.enum.slice(0, 8).map((v) => (
                        <span key={v} className="text-[9px] bg-neutral-800 text-neutral-400 px-1 py-0.5 rounded font-mono">{v}</span>
                      ))}
                      {param.enum.length > 8 && <span className="text-[9px] text-neutral-600">+{param.enum.length - 8}</span>}
                    </div>
                  )}
                </div>
                {param.description && (
                  <p className="text-[11px] text-neutral-500 mt-0.5 leading-relaxed">{param.description}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
